import React, { useState } from "react";
import PropTypes from "prop-types";
import styles from "./ContentSettingsCard.module.css";

// Additional IBOs are saved as one comma-separated string (the existing
// contentSettings.additionalIbos format), shown here as removable pills —
// same interaction as the WWG Store admin.
export const parseIboList = (value) =>
  Array.from(
    new Set(
      String(value || "")
        .split(/[\s,]+/)
        .map((ibo) => ibo.trim())
        .filter(Boolean),
    ),
  );

export const formatIboList = (ibos) => ibos.join(",");

export function AdditionalIbosField({ value, onChange }) {
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const ibos = parseIboList(value);

  const handleAdd = () => {
    // Accept one IBO or several pasted at once (comma/space separated).
    const added = parseIboList(input);
    setInput("");
    setError("");
    if (added.length === 0) return;
    const next = parseIboList(formatIboList([...ibos, ...added]));
    if (next.length !== ibos.length) onChange(formatIboList(next));
  };

  const handleRemove = (ibo) =>
    onChange(formatIboList(ibos.filter((existing) => existing !== ibo)));

  const handleInputChange = (event) => {
    const value = event.target.value;
    setInput(value);
    
    // Validate: only numbers allowed, no special characters or spaces
    if (value && !/^\d*$/.test(value)) {
      setError("Please enter numbers only.");
    } else {
      setError("");
    }
  };

  return (
    <div>
      <div className={styles.iboInputRow}>
        <input
          type="text"
          className={styles.textInput}
          placeholder="IBO #"
          aria-label="IBO number"
          value={input}
          onChange={handleInputChange}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !error) {
              event.preventDefault();
              handleAdd();
            }
          }}
        />
        <button
          type="button"
          className={styles.iboAddButton}
          onClick={handleAdd}
          disabled={!input.trim() || !!error}
        >
          + Add
        </button>
      </div>
      {error && (
        <p className={styles.iboError}>{error}</p>
      )}
      {ibos.length > 0 && (
        <div className={styles.iboList}>
          {ibos.map((ibo) => (
            <span key={ibo} className={styles.iboChip}>
              {ibo}
              <button
                type="button"
                className={styles.iboRemove}
                aria-label={`Remove ${ibo}`}
                onClick={() => handleRemove(ibo)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

AdditionalIbosField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

AdditionalIbosField.defaultProps = {
  value: "",
};

export default AdditionalIbosField;
